import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { BrandButton } from "@/components/ui/brand-button";
import {
	CheckCircle,
	Copy,
	FileCheck,
	FileX,
	FileWarning,
	ChevronDown,
	AlertCircle,
} from "lucide-react";
import { RootState, useAppDispatch, useAppSelector } from "@/lib/redux/root";

import { clearUploadState } from "@/lib/redux/slices/upload.slice";
import { clearOrderData } from "@/lib/redux/slices/order.slice";
import { getFolderIcon } from "@/lib/utils/common.utils";
import {
	FileUploadStatus,
	FileUploadStatusString,
} from "@/lib/redux/types/upload.types";
import { useBrand } from "@/lib/hooks/useBrand";

export default function SuccessPage() {
	const navigate = useNavigate();
	const params = useParams<{ slug: string; product: string }>();
	const [searchParams] = useSearchParams();
	const slug = params.slug as string;
	const {
		location: { appInfo },
	} = useAppSelector((state: RootState) => state.location);
	const { brandConfig } = useBrand();
	const [orderId, setOrderId] = useState("");
	const [copied, setCopied] = useState(false);
	const formData = useAppSelector((state: RootState) => state.uploadForm);

	const dispatch = useAppDispatch();

	const { files, progress } = useAppSelector(
		(state: RootState) => state.upload
	);

	const { uploadUrl } = useAppSelector((state: RootState) => state.location);

	const successFiles = files.filter(
		(f) => f.status === FileUploadStatus.Completed
	);
	const errorFiles = files.filter(
		(f) =>
			f.status === FileUploadStatus.Failed ||
			f.status === FileUploadStatus.UnsupportedFileType
	);

	const firstSuccessFile = successFiles[0];
	const successCount = successFiles.length;

	/* Presentation helpers: keep extension visible, middle-truncate long names */
	const formatFileName = (name: string, max = 28) => {
		if (!name) {
			return "";
		}
		if (name.length <= max) {
			return name;
		}
		const lastDot = name.lastIndexOf(".");
		const ext = lastDot > 0 ? name.slice(lastDot) : "";
		const base = lastDot > 0 ? name.slice(0, lastDot) : name;
		const keep = Math.max(4, Math.floor((max - ext.length) / 2));
		const start = base.slice(0, keep);
		const end = base.slice(-keep);
		return `${start}…${end}${ext}`;
	};

	const getStatusChip = (status: FileUploadStatus) => {
		if (status === FileUploadStatus.UnsupportedFileType) {
			return {
				label: FileUploadStatusString[status],
				bg: "bg-orange-50",
				text: "text-orange-700",
				Icon: FileWarning,
			};
		}
		return {
			label: FileUploadStatusString[status],
			bg: "bg-red-50",
			text: "text-red-700",
			Icon: FileX,
		};
	};

	useEffect(() => {
		const orderIdParam = searchParams.get("orderId");

		if (orderIdParam) {
			setOrderId(orderIdParam);
		} else {
			navigate(uploadUrl);
		}
	}, [searchParams, navigate, slug, uploadUrl]);

	/* Copy Order ID */
	const handleCopyOrderId = useCallback(() => {
		navigator.clipboard.writeText(orderId);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	}, [orderId]);

	/* Upload More */
	const handleUploadMore = useCallback(() => {
		dispatch(clearOrderData());
		dispatch(clearUploadState());
		navigate(`/${params.product}/${params.slug}`);
	}, [navigate, params, dispatch]);

	/* Locations */
	const handleViewLocations = useCallback(() => {
		window.open("https://www.e-arc.com/locations", "_blank");
	}, []);

	if (!orderId) {
		return null;
	}

	return (
		<main className='flex min-h-screen items-center justify-center px-0 py-0 sm:px-6 sm:py-12'>
			<Card className='animate-in fade-in zoom-in-95 relative h-screen w-screen border-none bg-white p-6 shadow-none duration-200 sm:h-auto sm:w-full sm:max-w-3xl sm:p-12 sm:shadow-2xl'>
				<div className='flex flex-col items-center space-y-10'>
					{/* SUCCESS ICON */}

					<img src='/Check Circle.svg' alt='Logo' className='h-20 w-auto' />

					{/* MESSAGE */}
					<div className='text-center'>
						<h1 className='mb-3 text-3xl font-bold text-gray-900 md:text-4xl'>
							Your order is placed successfully!
						</h1>
						<p className='text-lg text-gray-600'>
							We've received order. You'll receive a confirmation email shortly.
						</p>
					</div>

					{/* ORDER ID */}
					<div className='flex items-center gap-3'>
						<span className='text-lg text-gray-700'>Order ID :</span>
						<span className='text-lg font-bold text-gray-900'>#{orderId}</span>
						<button
							onClick={handleCopyOrderId}
							className='rounded p-2 text-gray-600 transition-colors hover:bg-gray-100'
							aria-label='Copy Order ID'
						>
							<Copy className='h-5 w-5' />
						</button>
						{copied && <span className='text-sm text-green-600'>Copied!</span>}
					</div>

					<div className='flex items-center gap-3'>
						<span className='text-lg text-gray-700'>Purpose :</span>
						<span className='text-lg font-bold text-gray-900'>
							{formData.purposeName}
						</span>
					</div>

					{/* UPLOAD SUMMARY */}

					<div className='w-full rounded-lg bg-gray-50 p-4'>
						{/* TOP SUCCESS MAIN FILE */}
						{firstSuccessFile && (
							<div className='flex items-center justify-between gap-3 border-b border-gray-100 pb-3'>
								<div className='flex min-w-0 items-center gap-3'>
									{/* icon */}
									<div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-white shadow-sm'>
										{typeof getFolderIcon(firstSuccessFile.name) === "function"
											? getFolderIcon(firstSuccessFile.name)()
											: getFolderIcon(firstSuccessFile.name)}
									</div>

									<div className='min-w-0'>
										<p
											className='truncate text-sm font-medium text-gray-900'
											title={firstSuccessFile.name}
											aria-label={firstSuccessFile.name}
										>
											{formatFileName(firstSuccessFile.name, 36)}
										</p>
										{successCount > 1 && (
											<p className='mt-0.5 text-xs text-green-600'>
												+ {successCount - 1} files successfully uploaded
											</p>
										)}
									</div>
								</div>

								{/* <button
									className='ml-2 text-gray-400 hover:text-gray-700'
									aria-label='Expand files'
								>
									<ChevronDown className='h-5 w-5' />
								</button> */}
							</div>
						)}

						{/* FAILED / UNSUPPORTED FILES */}
						{errorFiles.length > 0 && (
							<div className='mt-3'>
								<div
									className='max-h-40 space-y-2 pr-2'
									aria-live='polite'
									style={{
										maxHeight: 160,
										overflowY: "auto",
										WebkitOverflowScrolling: "touch",
									}}
								>
									{errorFiles.map((file, index) => {
										const chip = getStatusChip(file.status as FileUploadStatus);
										const Icon = chip.Icon;
										return (
											<div
												key={index}
												className='flex items-center justify-between gap-3 rounded-md bg-white px-3 py-2 shadow-sm'
											>
												{/* Left */}
												<div className='flex min-w-0 items-center gap-3'>
													<div className='flex h-9 w-9 items-center justify-center rounded bg-red-50'>
														<AlertCircle className='h-5 w-5 text-red-500' />
													</div>
													<p
														className='truncate text-sm text-gray-800'
														title={file.name}
														aria-label={file.name}
													>
														{formatFileName(file.name, 30)}
													</p>
												</div>

												{/* Right */}
												<div className='flex items-center gap-3'>
													<span
														className={`${chip.bg} ${chip.text} inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium`}
														role='status'
														aria-label={chip.label}
													>
														{chip.label}
													</span>
													<p className='text-xs text-gray-500'>
														{file.status ===
														FileUploadStatus.UnsupportedFileType
															? "Unsupported format"
															: "Upload incomplete"}
													</p>
												</div>
											</div>
										);
									})}
								</div>
							</div>
						)}
					</div>

					{/* ACTION BUTTONS */}
					{/* ACTION BUTTONS */}
					<div className='flex w-full flex-row gap-4 pt-4 sm:flex-wrap sm:justify-center'>
						<BrandButton
							onClick={handleUploadMore}
							className='h-12 flex-1 rounded-full text-sm sm:h-14 sm:flex-none sm:px-10 sm:text-lg'
						>
							Upload More
						</BrandButton>

						<BrandButton
							onClick={handleViewLocations}
							variant='outline'
							className='h-12 flex-1 rounded-full border-2 text-sm hover:text-white sm:h-14 sm:flex-none sm:px-10 sm:text-lg'
							style={{
								borderColor: appInfo?.primaryColor || brandConfig.primaryColor,
								color: appInfo?.textColor || brandConfig.textColor,
							}}
							onMouseEnter={(e) => {
								e.currentTarget.style.backgroundColor =
									appInfo?.primaryColor || brandConfig.primaryColor;
								e.currentTarget.style.color = "white";
							}}
							onMouseLeave={(e) => {
								e.currentTarget.style.backgroundColor = "transparent";
								e.currentTarget.style.color =
									appInfo?.textColor || brandConfig.textColor;
							}}
						>
							Locations
						</BrandButton>
					</div>
				</div>
			</Card>
		</main>
	);
}
